import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, CreditCard, Gift, Banknote, Truck, MapPin, Package,
  ChevronLeft, Check, Lock, AlertCircle, X, Plus, Minus, Loader2, ExternalLink,
  Ticket, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  calculateSubtotal, calculateCartTax, calculateTotal, 
  isEligibleForFreeDelivery, DELIVERY_FEE, FREE_DELIVERY_THRESHOLD, 
  TAX_RATE, COD_MIN_ORDERS 
} from '../data/mockData';
import { toast } from 'sonner';
import { ordersAPI, stripeAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import RaffleTicketPortal from '../components/RaffleTicketPortal';
import RaffleFloatingButton from '../components/RaffleFloatingButton';
import GuestCheckoutPrompt from '../components/GuestCheckoutPrompt';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Checkout = ({ cart, setCart, user, userOrderCount = 0, onBack, onOrderComplete, onSignIn }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [orderType, setOrderType] = useState('delivery'); // 'delivery' or 'pickup'
  const [deliveryType, setDeliveryType] = useState('standard'); // priority, same_day, next_day, standard
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardApplied, setGiftCardApplied] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showRafflePortal, setShowRafflePortal] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(!user); // Show if not logged in
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  
  // E-Transfer state
  const [etransferConfirmation, setEtransferConfirmation] = useState('');
  const [etransferScreenshot, setEtransferScreenshot] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  
  // Delivery zone state
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [currentZone, setCurrentZone] = useState(null);
  const [calculatedFee, setCalculatedFee] = useState(null);
  
  // Delivery time selection
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState('');
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  const [deliveryInfo, setDeliveryInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: 'Ottawa',
    province: 'ON',
    postalCode: '',
    instructions: '',
  });

  // Check if cart contains gift cards
  const cartHasGiftCards = cart.some(item => item.isGiftCard);

  // Reset payment method when order type changes or if cart has gift cards
  useEffect(() => {
    if (orderType === 'delivery') {
      // For delivery, default to card payment (not cash at pickup)
      if (paymentMethod === 'cash' || paymentMethod === 'cash_on_pickup') {
        setPaymentMethod('card');
      }
    }
    // If cart has gift cards and user selected giftcard payment, switch to card
    if (cartHasGiftCards && paymentMethod === 'giftcard') {
      setPaymentMethod('card');
      toast.info('Gift cards cannot be used to purchase other gift cards');
    }
  }, [orderType, cartHasGiftCards, paymentMethod]);

  // Generate available delivery dates based on delivery type
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    if (deliveryType === 'priority') {
      // Today only for priority
      dates.push({ date: today.toISOString().split('T')[0], label: 'Today (2-4 hours)' });
    } else if (deliveryType === 'same_day') {
      // Today only
      dates.push({ date: today.toISOString().split('T')[0], label: 'Today' });
    } else if (deliveryType === 'next_day') {
      // Tomorrow only
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      dates.push({ date: tomorrow.toISOString().split('T')[0], label: 'Tomorrow' });
    } else {
      // Standard: 3-5 business days
      for (let i = 3; i <= 7; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        // Skip weekends
        if (d.getDay() !== 0 && d.getDay() !== 6) {
          dates.push({ 
            date: d.toISOString().split('T')[0], 
            label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          });
        }
      }
    }
    return dates;
  };

  // Time slots
  const timeSlots = [
    { value: '09:00-12:00', label: 'Morning (9 AM - 12 PM)' },
    { value: '12:00-15:00', label: 'Afternoon (12 PM - 3 PM)' },
    { value: '15:00-18:00', label: 'Late Afternoon (3 PM - 6 PM)' },
    { value: '18:00-21:00', label: 'Evening (6 PM - 9 PM)' },
  ];

  // Fetch slot availability when date changes
  useEffect(() => {
    const fetchSlotAvailability = async () => {
      if (!deliveryDate || orderType !== 'delivery') {
        setSlotAvailability(null);
        return;
      }
      
      setLoadingSlots(true);
      try {
        const res = await fetch(`${API_URL}/api/delivery/slots/availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            date: deliveryDate,
            zone_code: currentZone?.code || null
          })
        });
        if (res.ok) {
          const data = await res.json();
          setSlotAvailability(data);
          
          // Clear selected slot if it's now full
          if (deliveryTimeSlot) {
            const selectedSlot = data.slots?.find(s => s.time_slot === deliveryTimeSlot);
            if (selectedSlot?.is_full) {
              setDeliveryTimeSlot('');
              toast.warning('Your selected time slot is now full. Please choose another.');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching slot availability:', error);
      } finally {
        setLoadingSlots(false);
      }
    };
    
    fetchSlotAvailability();
  }, [deliveryDate, currentZone, orderType]);

  // Fetch delivery zones and types
  useEffect(() => {
    const fetchDeliveryConfig = async () => {
      try {
        const [zonesRes, typesRes] = await Promise.all([
          fetch(`${API_URL}/api/delivery/zones`),
          fetch(`${API_URL}/api/delivery/types`)
        ]);
        if (zonesRes.ok) setDeliveryZones(await zonesRes.json());
        if (typesRes.ok) setDeliveryTypes(await typesRes.json());
      } catch (error) {
        console.error('Error fetching delivery config:', error);
      }
    };
    fetchDeliveryConfig();
  }, []);

  // Calculate delivery fee when postal code or delivery type changes
  useEffect(() => {
    const calculateFee = async () => {
      if (orderType !== 'delivery' || !deliveryInfo.postalCode || deliveryInfo.postalCode.length < 3) {
        setCalculatedFee(null);
        setCurrentZone(null);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/delivery/calculate-fee`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            postal_code: deliveryInfo.postalCode,
            delivery_type: deliveryType,
            distance_km: 5.0,  // Default estimate
            address: deliveryInfo.address,  // For real distance calculation
            city: deliveryInfo.city || 'Ottawa'
          })
        });
        if (res.ok) {
          const data = await res.json();
          setCalculatedFee(data);
          setCurrentZone({ code: data.zone_code, name: data.zone_name });
          // Show if real distance was calculated
          if (data.distance_source === 'openrouteservice') {
            console.log(`Real distance calculated: ${data.distance_km} km`);
          }
        }
      } catch (error) {
        console.error('Error calculating fee:', error);
      }
    };
    
    const debounce = setTimeout(calculateFee, 500);
    return () => clearTimeout(debounce);
  }, [deliveryInfo.postalCode, deliveryInfo.address, deliveryInfo.city, deliveryType, orderType]);

  // Check for payment success/cancel on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const sessionId = urlParams.get('session_id');
    const orderId = urlParams.get('order_id');
    const paymentCancelled = urlParams.get('payment_cancelled');

    if (paymentSuccess === 'true' && sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId, orderId);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentCancelled === 'true') {
      toast.error(t('paymentFailed'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check if Stripe is enabled
  useEffect(() => {
    const checkStripe = async () => {
      try {
        const result = await stripeAPI.getPublicKey();
        setStripeEnabled(result.enabled && !!result.publishable_key);
      } catch (error) {
        console.error('Error checking Stripe:', error);
        setStripeEnabled(false);
      }
    };
    checkStripe();
  }, []);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, orderId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your orders.');
      return;
    }

    try {
      const status = await stripeAPI.getCheckoutStatus(sessionId);
      
      if (status.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Payment successful!');
        setCart([]);
        if (onOrderComplete && status.order_id) {
          onOrderComplete(status.order_id);
        }
        return;
      } else if (status.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, orderId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      toast.error('Error verifying payment');
    }
  };

  // Calculate totals
  const subtotal = calculateSubtotal(cart);
  const taxableItems = cart.filter(item => item.taxable);
  const tax = calculateCartTax(cart);
  
  // Use calculated delivery fee or fallback
  const deliveryFee = orderType === 'pickup' ? 0 : 
    (calculatedFee ? calculatedFee.total_fee : 
     (isEligibleForFreeDelivery(subtotal) ? 0 : DELIVERY_FEE));
  
  const giftCardDiscount = giftCardApplied?.amount || 0;
  const total = subtotal + tax + deliveryFee - giftCardDiscount;

  const canUseCOD = userOrderCount >= COD_MIN_ORDERS;

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      setCart(cart.filter(item => item.id !== productId));
    } else {
      setCart(cart.map(item => 
        item.id === productId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const removeItem = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const applyGiftCard = () => {
    if (giftCardCode.toLowerCase() === 'gift50') {
      setGiftCardApplied({ code: giftCardCode, amount: 50 });
      toast.success('Gift card applied! $50.00 discount');
    } else if (giftCardCode.toLowerCase() === 'gift25') {
      setGiftCardApplied({ code: giftCardCode, amount: 25 });
      toast.success('Gift card applied! $25.00 discount');
    } else {
      toast.error('Invalid gift card code');
    }
  };

  const processPayment = async () => {
    setIsProcessing(true);
    
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          taxable: item.taxable || false
        })),
        delivery_info: {
          name: deliveryInfo.name,
          email: deliveryInfo.email,
          phone: deliveryInfo.phone,
          address: orderType === 'pickup' ? 'Store Pickup' : deliveryInfo.address,
          city: deliveryInfo.city,
          province: deliveryInfo.province,
          postal_code: orderType === 'pickup' ? 'K1Z 6A7' : deliveryInfo.postalCode,
          instructions: deliveryInfo.instructions || null,
          delivery_zone: currentZone?.code || null
        },
        payment_method: paymentMethod,
        order_type: orderType,
        delivery_type: orderType === 'delivery' ? deliveryType : 'none',
        delivery_zone: currentZone?.code || null,
        delivery_date: orderType === 'delivery' ? deliveryDate : null,
        delivery_time_slot: orderType === 'delivery' ? deliveryTimeSlot : null,
        distance_km: 5.0,  // Default estimate
        subtotal: subtotal,
        tax: tax,
        delivery_fee: orderType === 'pickup' ? 0 : deliveryFee,
        total: total,
        gift_card_code: giftCardApplied?.code || null,
        gift_card_discount: giftCardApplied?.amount || 0,
        // E-Transfer details
        etransfer_confirmation: paymentMethod === 'etransfer' ? etransferConfirmation : null,
        etransfer_screenshot: paymentMethod === 'etransfer' ? etransferScreenshot : null
      };

      // Validate e-Transfer fields
      if (paymentMethod === 'etransfer') {
        if (!etransferConfirmation) {
          toast.error('Please enter e-Transfer confirmation number');
          setIsProcessing(false);
          return;
        }
        if (!etransferScreenshot) {
          toast.error('Please upload e-Transfer screenshot');
          setIsProcessing(false);
          return;
        }
      }

      const order = await ordersAPI.create(orderData);
      
      if (paymentMethod === 'card' && stripeEnabled) {
        const originUrl = window.location.origin;
        const checkoutResult = await stripeAPI.createCheckout(order.id, originUrl);
        window.location.href = checkoutResult.checkout_url;
        return;
      } else if (paymentMethod === 'tap_to_pay' && stripeEnabled) {
        // Create Payment Intent for Apple Pay / Google Pay
        try {
          const intentResult = await stripeAPI.createPaymentIntent(order.id);
          
          // For now, redirect to a payment page or use Stripe.js
          // In a full implementation, you'd use the Payment Request Button
          toast.info('Tap to Pay payment created. Redirecting to secure payment...');
          
          // Store intent for later confirmation
          sessionStorage.setItem('pending_payment_intent', JSON.stringify({
            payment_intent_id: intentResult.payment_intent_id,
            client_secret: intentResult.client_secret,
            order_id: order.id
          }));
          
          // For now, redirect to Stripe checkout as fallback
          const originUrl = window.location.origin;
          const checkoutResult = await stripeAPI.createCheckout(order.id, originUrl);
          window.location.href = checkoutResult.checkout_url;
          return;
        } catch (error) {
          console.error('Tap to Pay error:', error);
          toast.error('Tap to Pay failed. Please try another payment method.');
          setIsProcessing(false);
          return;
        }
      } else {
        let successMsg = `Order ${order.order_number || order.id.slice(0,8).toUpperCase()} placed!`;
        if (orderType === 'pickup') {
          successMsg += ` Your pickup code will be sent when ready.`;
        }
        if (paymentMethod === 'etransfer') {
          successMsg += ` E-Transfer pending verification.`;
        }
        toast.success(successMsg);
        setCart([]);
        if (onOrderComplete) {
          onOrderComplete(order.id);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl shadow-lg p-12">
          <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment...</h2>
          <p className="text-gray-500">Please wait while we confirm your payment with Stripe.</p>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Add some items to get started</p>
        <Button onClick={onBack} className="bg-blue-500 hover:bg-blue-600 text-white">
          Continue Shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
      {/* Guest Checkout Prompt */}
      <GuestCheckoutPrompt
        isOpen={showGuestPrompt && !user}
        onSignIn={() => {
          setShowGuestPrompt(false);
          if (onSignIn) onSignIn();
        }}
        onCreateAccount={() => {
          setShowGuestPrompt(false);
          if (onSignIn) onSignIn('register');
        }}
        onContinueAsGuest={() => {
          setShowGuestPrompt(false);
          setIsGuestCheckout(true);
          toast.info('Continuing as guest. Create an account anytime to earn raffle tickets!');
        }}
      />

      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-8">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-800">Checkout</h1>
        {isGuestCheckout && !user && (
          <Badge className="bg-amber-100 text-amber-700 text-xs">Guest</Badge>
        )}
      </div>

      {/* Guest Banner */}
      {isGuestCheckout && !user && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-800 text-sm sm:text-base">You&apos;re checking out as a guest</p>
              <p className="text-xs sm:text-sm text-gray-600">Sign up to save your details and track orders easily!</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => onSignIn && onSignIn('register')}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            Create Account
          </Button>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div 
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm sm:text-base ${
                step >= s ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > s ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : s}
            </div>
            {s < 3 && (
              <div className={`w-8 sm:w-20 h-1 ${step > s ? 'bg-blue-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-center gap-6 sm:gap-16 mb-4 sm:mb-8 text-xs sm:text-sm">
        <span className={step >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Review</span>
        <span className={step >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Delivery</span>
        <span className={step >= 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Payment</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Step 1: Review Cart */}
          {step === 1 && (
            <Card className="p-3 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Review Your Cart</h2>
              <div className="space-y-3 sm:space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 sm:gap-4 py-3 sm:py-4 border-b border-gray-100">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-800 text-sm sm:text-base truncate">{item.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500">
                            ${item.price.toFixed(2)} each
                            {item.taxable && (
                              <Badge className="ml-1 sm:ml-2 bg-yellow-100 text-yellow-700 text-xs">
                                +HST
                              </Badge>
                            )}
                          </p>
                        </div>
                        <button 
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        >
                          <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="font-semibold text-gray-800">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => setStep(2)} 
                className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white"
              >
                Continue to Delivery
              </Button>
            </Card>
          )}

          {/* Step 2: Delivery Info */}
          {step === 2 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Delivery Information
              </h2>

              {/* Order Type Selection */}
              <div className="mb-6">
                <Label className="text-base font-medium mb-3 block">Order Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setOrderType('delivery')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      orderType === 'delivery' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="order-type-delivery"
                  >
                    <Truck className={`w-6 h-6 mx-auto mb-2 ${orderType === 'delivery' ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className={`font-medium ${orderType === 'delivery' ? 'text-blue-700' : 'text-gray-700'}`}>Delivery</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {deliveryFee > 0 ? `$${deliveryFee.toFixed(2)} fee` : 'Free delivery'}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderType('pickup')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      orderType === 'pickup' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="order-type-pickup"
                  >
                    <Package className={`w-6 h-6 mx-auto mb-2 ${orderType === 'pickup' ? 'text-green-500' : 'text-gray-400'}`} />
                    <p className={`font-medium ${orderType === 'pickup' ? 'text-green-700' : 'text-gray-700'}`}>Store Pickup</p>
                    <p className="text-xs text-gray-500 mt-1">No delivery fee</p>
                  </button>
                </div>
                
                {/* Delivery info banner removed - was ASAP-247/365 LOGISTICS */}
              </div>

              {/* Store Pickup Info */}
              {orderType === 'pickup' && (
                <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Pickup Location
                  </h3>
                  <p className="text-sm text-green-700">UPLANDS FOOD MARKET</p>
                  <p className="text-sm text-green-600">1036 Merivale Rd, Ottawa, ON, K1Z 6A7</p>
                  <p className="text-sm text-green-600 mt-1">📞 +1 613 715 9147</p>
                  <p className="text-xs text-green-600 mt-2">You&apos;ll receive a pickup code when your order is ready.</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input 
                    value={deliveryInfo.name}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, name: e.target.value})}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={deliveryInfo.email}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, email: e.target.value})}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={deliveryInfo.phone}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, phone: e.target.value})}
                    placeholder="(613) 555-0123"
                    className="mt-1"
                  />
                </div>
                {orderType === 'delivery' && (
                  <>
                    <div>
                      <Label>Postal Code</Label>
                      <Input 
                        value={deliveryInfo.postalCode}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, postalCode: e.target.value})}
                        placeholder="K1A 0B1"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Street Address</Label>
                      <AddressAutocomplete
                        value={deliveryInfo.address}
                        onChange={(value) => setDeliveryInfo({...deliveryInfo, address: value})}
                        onSelect={(addressData) => {
                          setDeliveryInfo(prev => ({
                            ...prev,
                            address: addressData.address || addressData.fullAddress,
                            city: addressData.city || prev.city,
                            province: addressData.province || prev.province,
                            postalCode: addressData.postalCode || prev.postalCode,
                            lat: addressData.lat,
                            lng: addressData.lng
                          }));
                          toast.success('Address selected');
                        }}
                        placeholder="Start typing your address..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input 
                        value={deliveryInfo.city}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, city: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Province</Label>
                      <Input 
                        value={deliveryInfo.province}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, province: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Delivery Instructions (Optional)</Label>
                      <Input 
                        value={deliveryInfo.instructions}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, instructions: e.target.value})}
                        placeholder="Ring doorbell, leave at door, etc."
                        className="mt-1"
                      />
                    </div>

                    {/* Delivery Zone Display */}
                    {currentZone && (
                      <div className="md:col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">Delivery Zone:</span> {currentZone.name}
                        </p>
                      </div>
                    )}

                    {/* Delivery Type Selection */}
                    <div className="md:col-span-2">
                      <Label className="text-base font-medium mb-3 block">Delivery Speed</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { code: 'priority', name: 'Priority', time: '2-4 hours', price: '~$15+' },
                          { code: 'same_day', name: 'Same Day', time: 'Today', price: '~$12+' },
                          { code: 'next_day', name: 'Next Day', time: 'Tomorrow', price: '~$10+' },
                          { code: 'standard', name: 'Standard', time: '3-5 days', price: '~$8+' },
                        ].map(type => (
                          <button
                            key={type.code}
                            type="button"
                            onClick={() => setDeliveryType(type.code)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              deliveryType === type.code
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`delivery-type-${type.code}`}
                          >
                            <p className={`font-medium ${deliveryType === type.code ? 'text-blue-700' : 'text-gray-800'}`}>
                              {type.name}
                            </p>
                            <p className="text-xs text-gray-500">{type.time}</p>
                            <p className="text-xs font-medium text-gray-600">{type.price}</p>
                          </button>
                        ))}
                      </div>
                      
                      {/* Calculated Fee Breakdown */}
                      {calculatedFee && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                          <p className="font-medium text-gray-800 mb-1">Delivery Fee: ${calculatedFee.total_fee.toFixed(2)}</p>
                          <div className="text-xs text-gray-500 space-y-0.5">
                            <p>Base: ${calculatedFee.breakdown.base.toFixed(2)}</p>
                            <p>Distance ({calculatedFee.distance_km}km): ${calculatedFee.breakdown.distance.toFixed(2)}</p>
                            <p>Zone ({calculatedFee.zone_name}): ${calculatedFee.breakdown.zone.toFixed(2)}</p>
                          </div>
                        </div>
                      )}

                      {/* Delivery Date Selection */}
                      <div className="mt-4">
                        <Label className="text-sm font-medium mb-2 block">Preferred Delivery Date</Label>
                        <div className="flex flex-wrap gap-2">
                          {getAvailableDates().map(d => (
                            <button
                              key={d.date}
                              type="button"
                              onClick={() => setDeliveryDate(d.date)}
                              className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                                deliveryDate === d.date
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Delivery Time Slot Selection */}
                      {deliveryDate && (
                        <div className="mt-3">
                          <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                            Preferred Time Slot
                            {loadingSlots && (
                              <span className="text-xs text-gray-400 animate-pulse">Loading availability...</span>
                            )}
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {timeSlots.map(slot => {
                              // Get availability info for this slot
                              const slotInfo = slotAvailability?.slots?.find(s => s.time_slot === slot.value);
                              const isFull = slotInfo?.is_full || false;
                              const available = slotInfo?.total_available;
                              const isLowStock = available !== undefined && available > 0 && available <= 3;
                              
                              return (
                                <button
                                  key={slot.value}
                                  type="button"
                                  onClick={() => !isFull && setDeliveryTimeSlot(slot.value)}
                                  disabled={isFull}
                                  className={`px-3 py-2 rounded-lg text-sm border transition-all relative ${
                                    isFull
                                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : deliveryTimeSlot === slot.value
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <span className="block">{slot.label}</span>
                                  {isFull && (
                                    <span className="text-[10px] text-red-500 font-medium block">FULLY BOOKED</span>
                                  )}
                                  {isLowStock && !isFull && (
                                    <span className="text-[10px] text-orange-500 font-medium block">Only {available} slots left!</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          {slotAvailability && !slotAvailability.slots?.some(s => !s.is_full) && (
                            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              All time slots are fully booked for this date. Please select a different date.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-4 mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  Continue to Payment
                </Button>
              </div>
            </Card>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-500" />
                Payment Method
              </h2>

              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                {/* Credit/Debit Card via Stripe */}
                <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                  paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                      <CreditCard className="w-5 h-5 text-blue-500" />
                      <span className="font-medium">Credit / Debit Card</span>
                      <span className="ml-auto text-sm text-gray-500">Visa, Mastercard, Amex</span>
                    </Label>
                  </div>
                  
                  {paymentMethod === 'card' && (
                    <div className="mt-4 pl-8">
                      {stripeEnabled ? (
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Lock className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-800">Secure Payment via Stripe</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            You will be redirected to Stripe&apos;s secure checkout to complete your payment.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <ExternalLink className="w-3 h-3" />
                            <span>Opens in same window</span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center gap-2 text-yellow-700">
                            <AlertCircle className="w-4 h-4" />
                            <span className="font-medium">Stripe Not Configured</span>
                          </div>
                          <p className="text-sm text-yellow-600 mt-1">
                            Card payments are currently unavailable. Please ask the admin to configure Stripe in settings.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Apple Pay / Google Pay - Tap to Pay */}
                {stripeEnabled && (
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'tap_to_pay' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="tap_to_pay" id="tap_to_pay" />
                      <Label htmlFor="tap_to_pay" className="flex items-center gap-2 cursor-pointer flex-1">
                        <div className="flex items-center gap-1">
                          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                          </svg>
                          <span className="text-lg">Pay</span>
                        </div>
                        <span className="mx-2 text-gray-400">/</span>
                        <div className="flex items-center gap-1">
                          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                          <span className="text-lg">Pay</span>
                        </div>
                        <span className="ml-auto text-sm text-purple-600 font-medium">Tap to Pay</span>
                      </Label>
                    </div>
                    
                    {paymentMethod === 'tap_to_pay' && (
                      <div className="mt-4 pl-8">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Lock className="w-4 h-4 text-purple-600" />
                            <span className="font-medium text-purple-800">Quick & Secure Payment</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            Use Apple Pay, Google Pay, or tap your card for instant payment.
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                              🍎 Apple Pay
                            </span>
                            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                              📱 Google Pay
                            </span>
                            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                              💳 Contactless
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Gift Card - Disabled if cart contains gift cards */}
                <div className={`border rounded-xl p-4 transition-all ${
                  cartHasGiftCards 
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                    : paymentMethod === 'giftcard' 
                      ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                      : 'border-gray-200 cursor-pointer'
                }`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem 
                      value="giftcard" 
                      id="giftcard" 
                      disabled={cartHasGiftCards}
                    />
                    <Label 
                      htmlFor="giftcard" 
                      className={`flex items-center gap-2 flex-1 ${cartHasGiftCards ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <Gift className="w-5 h-5 text-purple-500" />
                      <span className="font-medium">Gift Card</span>
                      {cartHasGiftCards && (
                        <Badge className="ml-2 bg-yellow-100 text-yellow-700 text-xs">
                          Not Available
                        </Badge>
                      )}
                    </Label>
                  </div>
                  
                  {cartHasGiftCards && (
                    <p className="mt-2 pl-8 text-xs text-yellow-700 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Gift cards cannot be used to purchase other gift cards
                    </p>
                  )}
                  
                  {paymentMethod === 'giftcard' && !cartHasGiftCards && (
                    <div className="mt-4 pl-8">
                      <div className="flex gap-2">
                        <Input 
                          value={giftCardCode}
                          onChange={(e) => setGiftCardCode(e.target.value)}
                          placeholder="Enter gift card code"
                          className="flex-1"
                        />
                        <Button onClick={applyGiftCard} variant="outline">
                          Apply
                        </Button>
                      </div>
                      {giftCardApplied && (
                        <p className="mt-2 text-green-600 text-sm flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          Gift card applied: -${giftCardApplied.amount.toFixed(2)}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Test codes: GIFT25, GIFT50
                      </p>
                    </div>
                  )}
                </div>

                {/* E-Transfer Payment */}
                <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                  paymentMethod === 'etransfer' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="etransfer" id="etransfer" />
                    <Label htmlFor="etransfer" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Banknote className="w-5 h-5 text-teal-500" />
                      <span className="font-medium">Interac e-Transfer</span>
                      <Badge className="ml-2 bg-teal-100 text-teal-700 text-xs">
                        Popular
                      </Badge>
                    </Label>
                  </div>
                  
                  {paymentMethod === 'etransfer' && (
                    <div className="mt-4 pl-8 space-y-4">
                      {/* E-Transfer Instructions */}
                      <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                        <h4 className="font-medium text-teal-800 mb-2">Send e-Transfer to:</h4>
                        <p className="text-teal-700 font-mono text-lg">payments@uplandsfoodmarket.com</p>
                        <p className="text-sm text-teal-600 mt-2">Amount: <strong>${total.toFixed(2)} CAD</strong></p>
                        <p className="text-xs text-teal-500 mt-2">
                          Please include your email or phone number in the message for verification.
                        </p>
                      </div>

                      {/* Confirmation Number */}
                      <div>
                        <Label className="text-sm font-medium">Confirmation Number *</Label>
                        <Input 
                          value={etransferConfirmation}
                          onChange={(e) => setEtransferConfirmation(e.target.value.toUpperCase())}
                          placeholder="e.g., CAFQ1234567890"
                          className="mt-1 font-mono"
                          data-testid="etransfer-confirmation-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter the confirmation number from your bank
                        </p>
                      </div>

                      {/* Screenshot Upload */}
                      <div>
                        <Label className="text-sm font-medium">Payment Screenshot *</Label>
                        <div className="mt-1">
                          {etransferScreenshot ? (
                            <div className="relative">
                              <img 
                                src={etransferScreenshot} 
                                alt="E-Transfer Screenshot" 
                                className="w-full h-40 object-cover rounded-lg border"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="absolute top-2 right-2"
                                onClick={() => setEtransferScreenshot(null)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-colors">
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                {uploadingScreenshot ? (
                                  <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                                ) : (
                                  <>
                                    <ExternalLink className="w-8 h-8 text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-500">Click to upload screenshot</p>
                                    <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                                  </>
                                )}
                              </div>
                              <input 
                                type="file" 
                                className="hidden" 
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('File too large. Max 5MB.');
                                      return;
                                    }
                                    setUploadingScreenshot(true);
                                    try {
                                      const reader = new FileReader();
                                      reader.onload = (event) => {
                                        setEtransferScreenshot(event.target.result);
                                        setUploadingScreenshot(false);
                                      };
                                      reader.readAsDataURL(file);
                                    } catch (error) {
                                      toast.error('Failed to upload screenshot');
                                      setUploadingScreenshot(false);
                                    }
                                  }
                                }}
                                data-testid="etransfer-screenshot-input"
                              />
                            </label>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Upload a screenshot of your e-Transfer confirmation for verification
                        </p>
                      </div>

                      {/* Validation Status */}
                      {etransferConfirmation && etransferScreenshot && (
                        <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                          <Check className="w-5 h-5" />
                          <span className="text-sm">Ready to submit - Vendor will verify your payment</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Cash on Delivery - Only for Pickup orders */}
                {orderType === 'pickup' && (
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } ${!canUseCOD ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="cod" id="cod" disabled={!canUseCOD} />
                      <Label htmlFor="cod" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Banknote className="w-5 h-5 text-green-500" />
                        <span className="font-medium">Cash on Delivery</span>
                        {!canUseCOD && (
                          <Badge className="ml-2 bg-gray-100 text-gray-600 text-xs">
                            After {COD_MIN_ORDERS} orders
                          </Badge>
                        )}
                      </Label>
                    </div>
                    
                    {!canUseCOD && (
                      <p className="mt-2 pl-8 text-sm text-gray-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Complete {COD_MIN_ORDERS - userOrderCount} more orders to unlock COD
                      </p>
                    )}
                  </div>
                )}

                {/* Cash on Pickup - Only for Pickup orders */}
                {orderType === 'pickup' && (
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'pickup' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="pickup" id="pickup" />
                      <Label htmlFor="pickup" className="flex items-center gap-2 cursor-pointer flex-1">
                        <MapPin className="w-5 h-5 text-orange-500" />
                        <span className="font-medium">Pay at Pickup (Cash/Card)</span>
                        <Badge className="ml-2 bg-green-100 text-green-700 text-xs">
                          Available
                        </Badge>
                      </Label>
                    </div>
                    
                    {paymentMethod === 'pickup' && (
                      <div className="mt-3 pl-8">
                        <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                          <p className="text-sm text-orange-800 font-medium">Store Pickup Address:</p>
                          <p className="text-sm text-orange-700 mt-1">
                            UPLANDS FOOD MARKET<br />
                            Merivale Road, Ottawa, ON<br />
                            Open: Mon-Sat 9AM-8PM, Sun 10AM-6PM
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </RadioGroup>

              <div className="flex gap-4 mt-6">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={processPayment}
                  disabled={isProcessing || (paymentMethod === 'card' && !stripeEnabled) || (paymentMethod === 'tap_to_pay' && !stripeEnabled)}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : paymentMethod === 'card' && stripeEnabled ? (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Pay ${total.toFixed(2)} with Stripe
                    </>
                  ) : paymentMethod === 'tap_to_pay' && stripeEnabled ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Tap to Pay ${total.toFixed(2)}
                    </>
                  ) : (
                    `Pay $${total.toFixed(2)}`
                  )}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Order Summary Sidebar */}
        <div className="lg:col-span-1">
          <Card className="p-6 sticky top-24">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              
              {taxableItems.length > 0 && (
                <div className="flex justify-between text-yellow-700">
                  <span>HST (13% on {taxableItems.length} taxable items)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery</span>
                {deliveryFee === 0 ? (
                  <span className="text-green-600 font-medium">FREE</span>
                ) : (
                  <span className="font-medium">${deliveryFee.toFixed(2)}</span>
                )}
              </div>

              {giftCardApplied && (
                <div className="flex justify-between text-green-600">
                  <span>Gift Card ({giftCardApplied.code})</span>
                  <span>-${giftCardApplied.amount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Free Delivery Progress */}
            {!isEligibleForFreeDelivery(subtotal) && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <Truck className="w-4 h-4 inline mr-1" />
                  Add ${(FREE_DELIVERY_THRESHOLD - subtotal).toFixed(2)} more for FREE delivery!
                </p>
                <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${Math.min((subtotal / FREE_DELIVERY_THRESHOLD) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Tax Info */}
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
              <p className="text-xs text-yellow-700">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                13% HST applies to beverages and snacks only. Most food items are tax-exempt.
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Raffle Ticket Portal Modal */}
      <RaffleTicketPortal 
        isOpen={showRafflePortal} 
        onClose={() => setShowRafflePortal(false)} 
        user={user}
      />
    </div>
  );
};

export default Checkout;
